import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { batchesService } from '../../services/metalguard';

import PrimaryNavBar from 'components/ui/PrimaryNavBar';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';
import { useSubscriptionStatus, SUB_STATUS } from '../../hooks/useSubscriptionStatus';
import SubscriptionBanner from 'components/SubscriptionBanner';
import { SkeletonRow } from '../../components/skeletons';

import BatchFilters from './components/BatchFilters';
import BatchTable from './components/BatchTable';
import BatchMobileCard from './components/BatchMobileCard';
import PerformanceAnalytics from './components/PerformanceAnalytics';
import BatchDetailModal from './components/BatchDetailModal';
import EditBatchModal from './components/EditBatchModal';
import StatusChangeModal from './components/StatusChangeModal';
import BulkActionsBar from './components/BulkActionsBar';
import BatchTimelineModal from 'components/BatchTimelineModal';
import Tooltip from 'components/Tooltip';
import { useToast } from 'components/Toast';

// Map DB batch to UI batch shape
function dbToUi(b) {
  const createdAt = b?.created_at ? new Date(b.created_at) : null;
  const now = new Date();
  const daysInInventory = createdAt ? Math.floor((now - createdAt) / (1000 * 60 * 60 * 24)) : null;
  return {
    id: b?.id,
    batchId: b?.batch_code || b?.id?.slice(0, 8)?.toUpperCase(),
    metal: b?.metal_name || '',
    category: b?.category_name || '',
    purchaseDate: createdAt ? createdAt?.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '',
    status: b?.batch_status,
    progress: b?.progress || 0,
    expectedMargin: b?.expected_margin != null ? parseFloat(b?.expected_margin) : null,
    actualMargin: b?.actual_margin != null ? parseFloat(b?.actual_margin) : null,
    daysRemaining: b?.days_remaining,
    daysInInventory,
    weight: b?.weight_t != null ? parseFloat(b?.weight_t) : null,
    weightT: b?.weight_t != null ? parseFloat(b?.weight_t) : null,
    purchasePrice: b?.purchase_price != null ? parseFloat(b?.purchase_price) : null,
    maxBuyPrice: b?.max_buy_price != null ? parseFloat(b?.max_buy_price) : null,
    yieldEstimated: b?.yield_est != null ? parseFloat(b?.yield_est) : null,
    yieldActual: b?.yield_actual != null ? parseFloat(b?.yield_actual) : null,
    logisticsCost: b?.logistics_cost != null ? parseFloat(b?.logistics_cost) : null,
    notes: b?.notes || null,
    createdAt: b?.created_at,
    timeline: [],
    _raw: b,
  };
}

const STATUS_TABS = [
  { label: 'Todos', value: 'all', tooltip: 'Muestra todos los lotes sin importar su estado actual.' },
  { label: 'Activo', value: 'En Espera', tooltip: 'Lotes en espera de inicio de procesamiento.' },
  { label: 'Procesando', value: 'Procesando', tooltip: 'Lotes actualmente en proceso de refinado o transformación.' },
  { label: 'Vendido', value: 'Completado', tooltip: 'Lotes cuya venta ha sido completada y cerrada.' },
];

export default function ActiveBatchManagement() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [metalFilter, setMetalFilter] = useState('Todos');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sortField, setSortField] = useState('purchaseDate');
  const [sortDir, setSortDir] = useState('desc');
  const [selectedIds, setSelectedIds] = useState([]);
  const [detailBatch, setDetailBatch] = useState(null);
  const [editBatch, setEditBatch] = useState(null);
  const [statusBatch, setStatusBatch] = useState(null);
  const [showAnalytics, setShowAnalytics] = useState(false);
  const [notification, setNotification] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // batch to delete
  const [deleting, setDeleting] = useState(false);
  const [flashIds, setFlashIds] = useState(new Set());
  const [timelineBatch, setTimelineBatch] = useState(null);

  const showNotif = (msg, type = 'success') => {
    addToast(msg, type);
  };

  const flashRow = (id) => {
    setFlashIds(prev => new Set([...prev, id]));
    setTimeout(() => setFlashIds(prev => { const n = new Set(prev); n?.delete(id); return n; }), 1500);
  };

  const loadBatches = useCallback(async () => {
    setLoading(true);
    const { data, error } = await batchesService?.getAll();
    if (!error && data) setBatches(data?.map(dbToUi));
    setLoading(false);
  }, []);

  useEffect(() => { loadBatches(); }, [loadBatches]);

  // Real-time subscription with error recovery
  const { status: subStatus, subWarningBanner, retryNow } = useSubscriptionStatus({
    channelName: `batches_realtime_${user?.id}`,
    filter: {
      event: '*',
      schema: 'public',
      table: 'batches',
      filter: user?.id ? `user_id=eq.${user?.id}` : undefined,
    },
    enabled: !!user?.id,
    onData: (payload) => {
      if (payload?.eventType === 'INSERT') {
        const newBatch = dbToUi(payload?.new);
        setBatches(prev => [newBatch, ...prev]);
        flashRow(newBatch?.id);
      } else if (payload?.eventType === 'UPDATE') {
        // Merge payload.new into the existing raw object so partial payloads
        // (when REPLICA IDENTITY FULL is not yet active) don't wipe fields.
        setBatches(prev => prev?.map(b => {
          if (b?.id !== payload?.new?.id) return b;
          const mergedRaw = { ...(b?._raw || {}), ...payload?.new };
          const updated = dbToUi(mergedRaw);
          return updated;
        }));
        flashRow(payload?.new?.id);
      } else if (payload?.eventType === 'DELETE') {
        setBatches(prev => prev?.filter(b => b?.id !== payload?.old?.id));
      }
    },
    onReconnect: loadBatches,
  });

  // Real-time subscription for sales INSERT — updates matching batch status to 'Completado'
  useSubscriptionStatus({
    channelName: `sales_realtime_${user?.id}`,
    filter: {
      event: 'INSERT',
      schema: 'public',
      table: 'sales',
      filter: user?.id ? `user_id=eq.${user?.id}` : undefined,
    },
    enabled: !!user?.id,
    onData: (payload) => {
      if (payload?.eventType === 'INSERT' && payload?.new?.batch_id) {
        const batchId = payload?.new?.batch_id;
        setBatches(prev => prev?.map(b => {
          if (b?.id !== batchId) return b;
          const mergedRaw = { ...(b?._raw || {}), batch_status: 'Completado' };
          return dbToUi(mergedRaw);
        }));
        flashRow(batchId);
      }
    },
    onReconnect: loadBatches,
  });

  const realtimeConnected = subStatus === SUB_STATUS?.CONNECTED;

  const handleSort = useCallback((field) => {
    setSortField(prev => {
      if (prev === field) { setSortDir(d => d === 'asc' ? 'desc' : 'asc'); return field; }
      setSortDir('asc');
      return field;
    });
  }, []);

  const filteredBatches = useMemo(() => {
    let result = [...batches];
    if (searchQuery) {
      const q = searchQuery?.toLowerCase();
      result = result?.filter(b => b?.batchId?.toLowerCase()?.includes(q) || b?.metal?.toLowerCase()?.includes(q) || b?.category?.toLowerCase()?.includes(q));
    }
    if (activeFilter !== 'all') result = result?.filter(b => b?.status === activeFilter);
    if (metalFilter !== 'Todos') result = result?.filter(b => b?.metal === metalFilter);
    result?.sort((a, b) => {
      let aVal = a?.[sortField], bVal = b?.[sortField];
      if (sortField === 'purchaseDate') {
        const parseDate = d => { if (!d) return new Date(0); const [day, mon, yr] = d?.split('/'); return new Date(yr, mon - 1, day); };
        aVal = parseDate(a?.purchaseDate); bVal = parseDate(b?.purchaseDate);
      }
      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return result;
  }, [batches, searchQuery, activeFilter, metalFilter, sortField, sortDir]);

  const handleSelectAll = (checked) => setSelectedIds(checked ? filteredBatches?.map(b => b?.id) : []);
  const handleSelectOne = (id, checked) => setSelectedIds(prev => checked ? [...prev, id] : prev?.filter(x => x !== id));

  const handleSaveEdit = async (updated) => {
    const rawBatch = batches?.find(b => b?.id === updated?.id)?._raw;
    const weightT = updated?.weight ? updated?.weight / 1000 : parseFloat(rawBatch?.weight_t) || 0;
    const yieldActual = updated?.yieldActual != null ? updated?.yieldActual : null;
    const yieldEst = updated?.yieldEstimated != null ? updated?.yieldEstimated : parseFloat(rawBatch?.yield_est) || 90;
    const yieldForCalc = yieldActual ?? yieldEst ?? 90;
    const originalRecoverable = weightT * (parseFloat(rawBatch?.yield_actual ?? rawBatch?.yield_est ?? 90) / 100);
    const currentWeightRemaining = parseFloat(rawBatch?.weight_remaining);
    const hasPartialSales = !isNaN(currentWeightRemaining) && Math.abs(currentWeightRemaining - originalRecoverable) > 0.0001;

    const updatePayload = {
      batch_code: updated?.batchId,
      metal_name: updated?.metal,
      category_name: updated?.category,
      weight_t: weightT || null,
      purchase_price: updated?.purchasePrice,
      yield_est: updated?.yieldEstimated,
      yield_actual: yieldActual,
      logistics_cost: updated?.logisticsCost,
      expected_margin: updated?.expectedMargin,
      actual_margin: updated?.actualMargin,
      notes: updated?.notes,
    };

    if (!hasPartialSales && weightT) {
      updatePayload.weight_remaining = weightT * (yieldForCalc / 100);
    }

    const { error } = await batchesService?.update(updated?.id, updatePayload);
    if (!error) {
      setBatches(prev => prev?.map(b => b?.id === updated?.id ? { ...b, ...updated } : b));
      showNotif(`Lote ${updated?.batchId} actualizado correctamente.`);
    } else {
      showNotif('Error al actualizar el lote.', 'error');
    }
    setEditBatch(null);
    setDetailBatch(null);
  };

  const handleConfirmStatus = async (id, newStatus) => {
    const { data, error } = await batchesService?.update(id, { batch_status: newStatus });
    if (!error) {
      setBatches(prev => prev?.map(b => {
        if (b?.id !== id) return b;
        if (data) {
          const mergedRaw = { ...(b?._raw || {}), ...data };
          return dbToUi(mergedRaw);
        }
        return { ...b, status: newStatus };
      }));
      addToast(`Estado actualizado a ${newStatus}`, 'success');
    } else {
      addToast('Error al actualizar el estado.', 'error');
    }
    setStatusBatch(null);
    setDetailBatch(null);
  };

  const handleDeleteBatch = async () => {
    if (!deleteConfirm) return;
    setDeleting(true);
    const { error } = await batchesService?.delete(deleteConfirm?.id);
    if (!error) {
      setBatches(prev => prev?.filter(b => b?.id !== deleteConfirm?.id));
      showNotif(`Lote ${deleteConfirm?.batchId} eliminado.`);
    } else {
      showNotif('Error al eliminar el lote.', 'error');
    }
    setDeleting(false);
    setDeleteConfirm(null);
  };

  const handleBulkStatusChange = () => {
    if (selectedIds?.length === 0) return;
    const first = batches?.find(b => b?.id === selectedIds?.[0]);
    if (first) setStatusBatch(first);
  };

  const handleBulkExport = () => {
    showNotif(`Exportando ${selectedIds?.length} lote(s)...`, 'info');
    setSelectedIds([]);
  };

  const handleClearFilters = () => {
    setSearchQuery(''); setActiveFilter('all'); setMetalFilter('Todos');
    setDateFrom(''); setDateTo('');
  };

  const summaryStats = useMemo(() => ({
    total: batches?.length,
    processing: batches?.filter(b => b?.status === 'Procesando')?.length,
    readyForSale: batches?.filter(b => b?.status === 'Listo para Venta')?.length,
    completed: batches?.filter(b => b?.status === 'Completado')?.length,
  }), [batches]);

  const getEmptyVariant = () => {
    if (searchQuery?.trim()) return 'search';
    if (activeFilter !== 'all') return 'filtered';
    return 'no-batches';
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <PrimaryNavBar />
      {notification && (
        <div className={`fixed top-20 right-4 z-[300] flex items-center gap-3 px-4 py-3 rounded-xl shadow-lg text-white text-sm spring-in ${notification?.type === 'success' ? 'bg-emerald-600' : notification?.type === 'info' ? 'bg-blue-600' : 'bg-red-600'}`}
          style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
          <Icon name={notification?.type === 'success' ? 'CheckCircle' : 'Info'} size={16} color="currentColor" />
          {notification?.msg}
        </div>
      )}
      <main className="px-4 md:px-6 lg:px-8 py-6 max-w-[1600px] mx-auto">
        {/* Page Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2 text-sm text-slate-500 mb-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
              <button onClick={() => navigate('/main-dashboard')} className="hover:text-orange-600 transition-colors">Dashboard</button>
              <Icon name="ChevronRight" size={14} color="currentColor" />
              <span className="text-slate-700 font-medium">Gestion de Lotes</span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900" style={{ fontFamily: '"Outfit", sans-serif' }}>Gestion de Lotes Activos</h1>
            <p className="text-sm text-slate-500 mt-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
              Seguimiento y control de lotes en procesamiento — {new Date()?.toLocaleDateString('es-ES', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' })}
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Tooltip description="Muestra u oculta el panel lateral de análisis de rendimiento con KPIs y gráficos de yield y márgenes.">
              <Button variant="outline" size="sm" iconName={showAnalytics ? 'BarChart2' : 'BarChart2'} iconPosition="left" onClick={() => setShowAnalytics(v => !v)}>
                {showAnalytics ? 'Ocultar Analisis' : 'Ver Analisis'}
              </Button>
            </Tooltip>
            <Tooltip description="Descarga un informe en formato exportable con los datos de todos los lotes activos.">
              <Button variant="outline" size="sm" iconName="Download" iconPosition="left" onClick={() => showNotif('Exportando informe...', 'info')}>Exportar</Button>
            </Tooltip>
            <Tooltip description="Abre la calculadora de lotes para registrar un nuevo lote con precio máximo de compra calculado.">
              <Button variant="default" size="sm" iconName="Plus" iconPosition="left" onClick={() => navigate('/batch-calculator')}>Nuevo Lote</Button>
            </Tooltip>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            { label: 'Total Lotes', value: summaryStats?.total, icon: 'Layers', color: 'text-slate-600 bg-slate-100', tooltip: 'Número total de lotes registrados en el sistema, en cualquier estado.' },
            { label: 'Procesando', value: summaryStats?.processing, icon: 'Loader', color: 'text-blue-600 bg-blue-100', tooltip: 'Lotes que están actualmente en proceso de refinado o transformación.' },
            { label: 'Listo para Venta', value: summaryStats?.readyForSale, icon: 'CheckCircle', color: 'text-emerald-600 bg-emerald-100', tooltip: 'Lotes que han completado el procesamiento y están disponibles para ser vendidos.' },
            { label: 'Vendidos', value: summaryStats?.completed, icon: 'Archive', color: 'text-slate-500 bg-slate-100', tooltip: 'Lotes cuya transacción de venta ha sido confirmada y cerrada.' },
          ]?.map(stat => (
            <div key={stat?.label} className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
              <div className={`p-2.5 rounded-lg ${stat?.color}`}>
                <Icon name={stat?.icon} size={18} color="currentColor" strokeWidth={2} />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800 font-mono leading-none">{loading ? '...' : stat?.value}</p>
                <Tooltip description={stat?.tooltip}>
                  <p className="text-xs text-slate-500 mt-0.5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{stat?.label}</p>
                </Tooltip>
              </div>
            </div>
          ))}
        </div>

        <SubscriptionBanner status={subStatus} subWarningBanner={subWarningBanner} retryNow={retryNow} />

        {loading ? (
          <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  {['', 'Lote', 'Metal', 'Estado', 'Peso', 'Precio', 'Margen', 'Días', 'Acciones']?.map(h => (
                    <th key={h} className="px-3 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {Array.from({ length: 6 })?.map((_, i) => (
                  <SkeletonRow key={i} cols={9} />
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className={`flex flex-col ${showAnalytics ? 'lg:flex-row' : ''} gap-6`}>
            <div className={`flex-1 min-w-0 space-y-4 ${showAnalytics ? 'lg:max-w-[calc(100%-380px)]' : ''}`}>
              {/* Status filter tabs */}
              <div className="flex items-center gap-1 flex-wrap">
                {STATUS_TABS?.map(tab => (
                  <Tooltip key={tab?.value} description={tab?.tooltip}>
                    <button
                      onClick={() => setActiveFilter(tab?.value)}
                      className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                        activeFilter === tab?.value
                          ? 'bg-orange-600 text-white' :'bg-white border border-slate-200 text-slate-600 hover:border-orange-300 hover:text-orange-600'
                      }`}
                      style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
                    >
                      {tab?.label}
                      {tab?.value !== 'all' && (
                        <span className={`ml-1.5 text-xs px-1.5 py-0.5 rounded-full ${
                          activeFilter === tab?.value ? 'bg-white/20 text-white' : 'bg-slate-100 text-slate-500'
                        }`}>
                          {batches?.filter(b => b?.status === tab?.value)?.length}
                        </span>
                      )}
                    </button>
                  </Tooltip>
                ))}
                {/* Live indicator */}
                <Tooltip description={realtimeConnected ? 'Conexión en tiempo real activa. Los cambios de estado se reflejan automáticamente sin recargar la página.' : 'Sin conexión en tiempo real. Los datos pueden no estar actualizados.'}>
                  <div className="ml-auto flex items-center gap-1.5 cursor-help">
                    <span className={`w-2 h-2 rounded-full ${realtimeConnected ? 'bg-emerald-500 animate-pulse' : 'bg-slate-300'}`} />
                    <span className="text-xs text-slate-500" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
                      {realtimeConnected ? 'En vivo' : 'Desconectado'}
                    </span>
                  </div>
                </Tooltip>
              </div>

              <BatchFilters
                searchQuery={searchQuery} setSearchQuery={setSearchQuery}
                activeFilter={activeFilter} setActiveFilter={setActiveFilter}
                metalFilter={metalFilter} setMetalFilter={setMetalFilter}
                dateFrom={dateFrom} setDateFrom={setDateFrom}
                dateTo={dateTo} setDateTo={setDateTo}
                resultCount={filteredBatches?.length}
                onClearFilters={handleClearFilters}
              />

              {/* Empty state */}
              {filteredBatches?.length === 0 && !loading && (
                <div className="bg-white border border-slate-200 rounded-xl p-12 flex flex-col items-center gap-4 text-slate-400">
                  <Icon name="Inbox" size={48} color="currentColor" strokeWidth={1.5} />
                  <div className="text-center">
                    <p className="text-base font-medium text-slate-600" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>No hay lotes</p>
                    <p className="text-sm mt-1" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Crea tu primer lote desde la calculadora</p>
                  </div>
                  <button
                    onClick={() => navigate('/batch-calculator')}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors"
                    style={{ background: 'var(--color-accent)', fontFamily: '"IBM Plex Sans", sans-serif' }}
                  >
                    <Icon name="Calculator" size={15} color="white" />
                    Ir a Calculadora de Lotes
                  </button>
                </div>
              )}

              {filteredBatches?.length > 0 && (
                <>
                  <div className="hidden md:block">
                    <BatchTable
                      batches={filteredBatches}
                      selectedIds={selectedIds}
                      onSelectAll={handleSelectAll}
                      onSelectOne={handleSelectOne}
                      sortField={sortField}
                      sortDir={sortDir}
                      onSort={handleSort}
                      onViewDetail={setDetailBatch}
                      onEditBatch={setEditBatch}
                      onStatusChange={setStatusBatch}
                      onDeleteBatch={setDeleteConfirm}
                      onViewTimeline={setTimelineBatch}
                      flashIds={flashIds}
                      emptyVariant={getEmptyVariant()}
                      onClearFilter={() => setActiveFilter('all')}
                      onClearSearch={() => setSearchQuery('')}
                      onNavigateCalculator={() => navigate('/batch-calculator')}
                    />
                  </div>
                  <div className="md:hidden space-y-3">
                    {filteredBatches?.map(batch => (
                      <BatchMobileCard
                        key={batch?.id}
                        batch={batch}
                        isSelected={selectedIds?.includes(batch?.id)}
                        onSelect={handleSelectOne}
                        onViewDetail={setDetailBatch}
                        onEditBatch={setEditBatch}
                        onStatusChange={setStatusBatch}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
            {showAnalytics && (
              <div className="lg:w-[360px] flex-shrink-0">
                <PerformanceAnalytics batches={batches} />
              </div>
            )}
          </div>
        )}
      </main>
      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }}>
          <div className="bg-white border border-slate-200 rounded-xl w-full max-w-sm p-6 shadow-xl">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Icon name="Trash2" size={18} color="#ef4444" strokeWidth={2} />
              </div>
              <div>
                <h3 className="font-semibold text-slate-900" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Eliminar lote</h3>
                <p className="text-sm text-slate-500" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>Esta acción no se puede deshacer</p>
              </div>
            </div>
            <p className="text-sm text-slate-600 mb-5" style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}>
              ¿Estás seguro de que deseas eliminar el lote <strong>{deleteConfirm?.batchId}</strong>?
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 border border-slate-200 rounded-lg hover:bg-slate-200 transition-colors"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
              >
                Cancelar
              </button>
              <button
                onClick={handleDeleteBatch}
                disabled={deleting}
                className="flex-1 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
                style={{ fontFamily: '"IBM Plex Sans", sans-serif' }}
              >
                {deleting ? (
                  <>
                    <div className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Eliminando...
                  </>
                ) : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
      {detailBatch && (
        <BatchDetailModal batch={detailBatch} onClose={() => setDetailBatch(null)} onEdit={(b) => { setDetailBatch(null); setEditBatch(b); }} onStatusChange={(b) => { setDetailBatch(null); setStatusBatch(b); }} />
      )}
      {editBatch && (
        <EditBatchModal batch={editBatch} onClose={() => setEditBatch(null)} onSave={handleSaveEdit} />
      )}
      {statusBatch && (
        <StatusChangeModal batch={statusBatch} onClose={() => setStatusBatch(null)} onConfirm={handleConfirmStatus} />
      )}
      {timelineBatch && (
        <BatchTimelineModal
          batchId={timelineBatch?.id}
          batchRaw={timelineBatch?._raw}
          onClose={() => setTimelineBatch(null)}
        />
      )}
      <BulkActionsBar
        selectedCount={selectedIds?.length}
        onBulkStatusChange={handleBulkStatusChange}
        onBulkExport={handleBulkExport}
        onClearSelection={() => setSelectedIds([])}
      />
      {subStatus === SUB_STATUS?.ERROR && (
        <div className="fixed inset-x-4 bottom-4 z-[299] flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg text-white text-sm bg-red-600">
          <Icon name="AlertTriangle" size={16} color="currentColor" />
          <div>
            <p className="font-medium text-slate-100">Error de conexión</p>
            <p className="text-sm text-slate-300">{subWarningBanner}</p>
            <button className="text-sm text-slate-100 hover:underline" onClick={retryNow}>Intentar de nuevo</button>
          </div>
        </div>
      )}
      <SubscriptionBanner />
    </div>
  );
}